<script setup>
import {computed, nextTick, onBeforeUnmount, onMounted, ref, useId, watch} from 'vue'
import tagOptionLabel from './_optionLabel.vue'

// Drop-in replacement for <tags-multi-select>: same props, events and exposed methods.
const props = defineProps({
    preSelectedTags: {
        type: Array,
        required: false,
        default: () => [],
    },
    allowCreation: {
        type: Boolean,
        required: false,
        default: true,
    },
    placeholder: {
        type: String,
        required: false,
        default: _mft('crm:tag.searchTags'),
    },
    selectMultiple: {
        type: Boolean,
        required: false,
        default: true,
    },
    displayCounts: {
        type: Array,
        required: false,
        default: () => [], // total, email, phone
    },
    // Kept for API compatibility with v1; options are always rendered as a list
    displayAs: {
        type: String,
        required: false,
        default: 'pill', // pill, list
    },
    inline: {
        type: Boolean,
        required: false,
        default: false,
    },
})

const emit = defineEmits(['tagsSelected', 'close', 'doReloadLeads'])

const listboxId = useId()

const root = ref(null)
const searchInput = ref(null)

const availableTags = ref([])
const selectedTags = ref([])
const loading = ref(true)
const creating = ref(false)
const search = ref('')
const open = ref(false)
const highlightedIndex = ref(-1)

// Collapse long selections to the first few chips until the user asks to see
// them all; once expanded it stays expanded, even as more tags are added
const COLLAPSED_SELECTED_LIMIT = 5
const showAllSelected = ref(false)

const syncSelectedTagsFromProps = () => {
    if (availableTags.value.length === 0) {
        return
    }

    const nextSelectedTags = props.preSelectedTags
        .map((tagIdentifier) => availableTags.value.find((tag) => (
            tag.id === tagIdentifier || tag.uuid === tagIdentifier
        )))
        .filter(Boolean)

    const currentSelectedTagIds = selectedTags.value.map((tag) => tag.id)
    const nextSelectedTagIds = nextSelectedTags.map((tag) => tag.id)

    if (JSON.stringify(currentSelectedTagIds) !== JSON.stringify(nextSelectedTagIds)) {
        selectedTags.value = nextSelectedTags
    }
}

const loadTags = () => {
    return axios.get(route('crm.leadtags.index')).then((response) => {
        availableTags.value = response.data.tags
        syncSelectedTagsFromProps()
    }).finally(() => {
        loading.value = false
    })
}

const searchTerm = computed(() => search.value.trim().toLowerCase())

const filteredTags = computed(() => {
    if (! searchTerm.value) {
        return availableTags.value
    }

    return availableTags.value.filter((tag) => tag.name.toLowerCase().includes(searchTerm.value))
})

const canCreate = computed(() => (
    props.allowCreation
    && searchTerm.value.length > 0
    && ! availableTags.value.some((tag) => tag.name.toLowerCase() === searchTerm.value)
))

// The "create" row sits after the tags, so it shares the same keyboard index space
const createOptionIndex = computed(() => filteredTags.value.length)
const optionCount = computed(() => filteredTags.value.length + (canCreate.value ? 1 : 0))

const optionId = (index) => `${listboxId}-option-${index}`

const visibleSelectedTags = computed(() => (
    showAllSelected.value ? selectedTags.value : selectedTags.value.slice(0, COLLAPSED_SELECTED_LIMIT)
))
const hiddenSelectedCount = computed(() => selectedTags.value.length - visibleSelectedTags.value.length)

const isSelected = (tag) =>selectedTags.value.some((selected) => selected.id === tag.id)

const chipStyle = (tag) => tag.color
    ? {backgroundColor: tag.color, color: tag.textcolor, borderColor: 'transparent'}
    : {}

const openDropdown = () => {
    if (open.value) {
        return
    }

    open.value = true
    highlightedIndex.value = -1
}

const closeDropdown = () => {
    open.value = false
    search.value = ''
    highlightedIndex.value = -1
}

const focusSearch = () => {
    searchInput.value?.focus()
}

// After picking from a search, select the term so typing starts a new search
// while the current results stay visible until then
const focusAndSelectSearch = () => {
    focusSearch()
    searchInput.value?.select()
}

const toggleTag = (tag) => {
    if (! props.selectMultiple) {
        selectedTags.value = [tag]
        closeDropdown()
        return
    }

    if (isSelected(tag)) {
        removeTag(tag)
    } else {
        selectedTags.value = [...selectedTags.value, tag]
    }
    focusAndSelectSearch()
}

const removeTag = (tag) => {
    selectedTags.value = selectedTags.value.filter((selected) => selected.id !== tag.id)
}

// "Select all" acts on the visible (filtered) tags, so it can be combined with search
const visibleSelectedCount = computed(() => filteredTags.value.filter(isSelected).length)
const allVisibleSelected = computed(() => (
    filteredTags.value.length > 0 && visibleSelectedCount.value === filteredTags.value.length
))
const someVisibleSelected = computed(() => visibleSelectedCount.value > 0 && ! allVisibleSelected.value)

const toggleSelectAll = () => {
    if (allVisibleSelected.value) {
        const visibleIds = filteredTags.value.map((tag) => tag.id)
        selectedTags.value = selectedTags.value.filter((selected) => ! visibleIds.includes(selected.id))
    } else {
        selectedTags.value = [
            ...selectedTags.value,
            ...filteredTags.value.filter((tag) => ! isSelected(tag)),
        ]
    }
    focusAndSelectSearch()
}

const clearSelection = () => {
    selectedTags.value = []
    focusSearch()
}

const clearSelectedTags = () => {
    selectedTags.value = []
    closeDropdown()
}

const createTag = () => {
    const name = search.value.trim()
    if (! canCreate.value || creating.value) {
        return
    }

    creating.value = true

    axios.post(route('crm.leadtags.store'), {name})
        .then(() => loadTags())
        .then(() => {
            const created = availableTags.value.find((tag) => tag.name.toLowerCase() === name.toLowerCase())
            search.value = ''
            if (created && ! isSelected(created)) {
                toggleTag(created)
            }
        })
        .finally(() => {
            creating.value = false
        })
}

const activateHighlighted = () => {
    const index = highlightedIndex.value
    if (index < 0 || index >= optionCount.value) {
        return
    }

    if (index === createOptionIndex.value && canCreate.value) {
        createTag()
    } else {
        toggleTag(filteredTags.value[index])
    }
}

const moveHighlight = (step) => {
    openDropdown()
    if (optionCount.value === 0) {
        return
    }

    const start = highlightedIndex.value === -1 && step < 0 ? 0 : highlightedIndex.value
    highlightedIndex.value = (start + step + optionCount.value) % optionCount.value
}

const onSearchKeydown = (e) => {
    switch (e.key) {
        case 'ArrowDown':
            e.preventDefault()
            moveHighlight(1)
            break
        case 'ArrowUp':
            e.preventDefault()
            moveHighlight(-1)
            break
        case 'Enter':
            e.preventDefault()
            activateHighlighted()
            break
        case 'Escape':
            // Only swallow Escape when it closes the dropdown, so a surrounding
            // panel or modal still gets to close on the next press
            if (open.value) {
                e.stopPropagation()
                closeDropdown()
            }
            break
        case 'Backspace':
            // The last selected tag may be collapsed; reveal it rather than removing it unseen
            if (search.value === '' && hiddenSelectedCount.value > 0) {
                showAllSelected.value = true
            } else if (search.value === '' && selectedTags.value.length > 0) {
                removeTag(selectedTags.value[selectedTags.value.length - 1])
            }
            break
        case 'Tab':
            closeDropdown()
            break
    }
}

const onFieldMousedown = (e) => {
    // Clicking the input itself only opens, so placing the caret never closes the list
    if (e.target === searchInput.value) {
        openDropdown()
        return
    }

    // Read the state before focusing: focus() synchronously fires the input's
    // focus handler, which opens the list and would make this toggle close it again
    const wasOpen = open.value

    // Keep focus in the search input instead of letting the field steal it
    e.preventDefault()
    focusSearch()
    wasOpen ? closeDropdown() : openDropdown()
}

const onPointerdown = (e) => {
    if (open.value && root.value && ! root.value.contains(e.target)) {
        closeDropdown()
    }
}

watch(search, () => {
    if (search.value !== '') {
        openDropdown()
    }
    highlightedIndex.value = searchTerm.value && optionCount.value > 0 ? 0 : -1
})

watch(highlightedIndex, (index) => {
    if (index < 0) {
        return
    }
    nextTick(() => {
        document.getElementById(optionId(index))?.scrollIntoView({block: 'nearest'})
    })
})

watch(
    [() => props.preSelectedTags, availableTags],
    () => {
        syncSelectedTagsFromProps()
    },
    {deep: true}
)

watch(selectedTags, () => {
    emit('tagsSelected', selectedTags.value)
}, {deep: true})

onMounted(() => {
    loadTags()
    document.addEventListener('pointerdown', onPointerdown)
})

onBeforeUnmount(() => {
    document.removeEventListener('pointerdown', onPointerdown)
})

defineExpose({clearSelectedTags})
</script>

<template>
    <div
        ref="root"
        class="relative"
    >
        <div
            class="flex items-center w-full gap-2 py-1.5 pl-2 pr-1.5 bg-white border rounded-md cursor-text min-h-[2.625rem] transition-colors"
            :class="open ? 'border-myflowPurple ring-1 ring-myflowPurple/20' : 'border-gray-300 hover:border-gray-400'"
            @mousedown="onFieldMousedown"
        >
            <div class="flex flex-wrap items-center flex-1 min-w-0 gap-1.5">
                <div
                    v-for="tag in visibleSelectedTags"
                    :key="tag.id"
                    :title="tag.description || null"
                >
                    <pill
                        class="bg-gray-50 border border-gray-100 text-black flex items-center space-x-0.5"
                        size="sm"
                    >
                        <div
                            v-if="tag.color"
                            class="relative w-2.5 h-2.5 mr-1"
                        >
                            <div
                                class="absolute inset-0 w-2.5 h-2.5 rounded-full"
                                :style="{backgroundColor: tag.color, color: tag.textcolor}"
                            ></div>
                        </div>
                        <span class="truncate">{{ tag.name }}</span>
                        <button
                            type="button"
                            class="flex items-center justify-center w-4 h-4 rounded opacity-60 hover:opacity-100 hover:bg-black/10"
                            :aria-label="`${_mft('shared:action.remove')} ${tag.name}`"
                            @mousedown.prevent.stop
                            @click.stop="removeTag(tag)"
                        >
                            <i class="fa-solid fa-xmark text-[10px]"></i>
                        </button>
                    </pill>
                </div>

                <button
                    v-if="hiddenSelectedCount > 0"
                    type="button"
                    class="px-2 py-1 text-xs font-medium text-gray-700 bg-transparent border border-gray-200 rounded-xl hover:border-gray-300"
                    @mousedown.prevent.stop
                    @click.stop="showAllSelected = true"
                >
                    {{ _mft('crm:tag.nMore', {count: hiddenSelectedCount}) }}
                </button>

                <input
                    ref="searchInput"
                    v-model="search"
                    type="text"
                    role="combobox"
                    autocomplete="off"
                    data-1p-ignore="true"
                    maxlength="24"
                    class="flex-1 min-w-[6rem] p-0 py-0.5 text-gray-900 bg-transparent border-0 outline-none focus:ring-0 placeholder:text-gray-400"
                    :placeholder="selectedTags.length > 0 ? '' : placeholder"
                    :aria-expanded="open"
                    :aria-controls="listboxId"
                    :aria-activedescendant="open && highlightedIndex >= 0 ? optionId(highlightedIndex) : null"
                    @focus="openDropdown"
                    @keydown="onSearchKeydown"
                />
            </div>

            <div class="flex items-center self-start gap-0.5 text-gray-400 h-7">
                <button
                    v-if="selectedTags.length > 0"
                    type="button"
                    class="flex items-center justify-center rounded-md w-7 h-7 hover:text-gray-600 hover:bg-gray-100"
                    :aria-label="_mft('shared:action.clear')"
                    @mousedown.prevent.stop
                    @click.stop="clearSelection"
                >
                    <i class="fa-solid fa-circle-xmark"></i>
                </button>
                <span class="flex items-center justify-center cursor-default w-7 h-7">
                    <i
                        class="text-xs transition-transform fa-solid fa-chevron-down"
                        :class="open ? 'rotate-180' : ''"
                    ></i>
                </span>
            </div>
        </div>

        <div
            v-if="open"
            class="mt-1 overflow-hidden bg-white border border-gray-200 rounded-xl"
            :class="inline ? '' : 'absolute left-0 right-0 z-50 shadow-xl top-full'"
        >
            <div
                v-if="! loading"
                class="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-100"
            >
                <button
                    v-if="selectMultiple"
                    type="button"
                    role="checkbox"
                    class="flex items-center gap-3 text-xs font-medium text-gray-700 disabled:opacity-50 disabled:cursor-default"
                    :aria-checked="allVisibleSelected ? 'true' : (someVisibleSelected ? 'mixed' : 'false')"
                    :disabled="filteredTags.length === 0"
                    @mousedown.prevent
                    @click="toggleSelectAll"
                >
                    <span
                        class="flex items-center justify-center w-4 h-4 border rounded shrink-0"
                        :class="allVisibleSelected || someVisibleSelected ? 'bg-myflowPurple border-myflowPurple text-white' : 'bg-white border-gray-300'"
                    >
                        <i
                            v-if="allVisibleSelected || someVisibleSelected"
                            class="fa-solid text-[10px]"
                            :class="allVisibleSelected ? 'fa-check' : 'fa-minus'"
                        ></i>
                    </span>
                    {{ _mft('crm:tag.selectAll') }}
                </button>
                <span class="ml-auto text-xs text-gray-500">
                    {{ _mft('crm:tag.nTags', {count: filteredTags.length}) }}
                </span>
            </div>

            <ul
                :id="listboxId"
                role="listbox"
                :aria-multiselectable="selectMultiple"
                class="py-1 overflow-y-auto max-h-64"
            >
                <li
                    v-if="loading"
                    class="px-3 py-2 text-sm text-gray-500"
                >
                    {{ _mft('shared:loading') }}
                </li>

                <template v-else>
                    <li
                        v-for="(tag, index) in filteredTags"
                        :id="optionId(index)"
                        :key="tag.id"
                        role="option"
                        :aria-selected="isSelected(tag)"
                        class="flex items-center gap-3 px-3 py-2 cursor-pointer"
                        :class="index === highlightedIndex ? 'bg-gray-100' : ''"
                        @mousedown.prevent
                        @mouseenter="highlightedIndex = index"
                        @click="toggleTag(tag)"
                    >
                        <span
                            v-if="selectMultiple"
                            class="flex items-center justify-center w-4 h-4 border rounded shrink-0"
                            :class="isSelected(tag) ? 'bg-myflowPurple border-myflowPurple text-white' : 'bg-white border-gray-300'"
                        >
                            <i
                                v-if="isSelected(tag)"
                                class="fa-solid fa-check text-[10px]"
                            ></i>
                        </span>

                        <tag-option-label
                            :tag="tag"
                            :emphasized="isSelected(tag)"
                            :display-counts="displayCounts"
                        />

                        <i
                            v-if="! selectMultiple && isSelected(tag)"
                            class="text-sm fa-solid fa-check text-myflowPurple shrink-0"
                        ></i>
                    </li>

                    <li
                        v-if="filteredTags.length === 0 && ! canCreate"
                        class="px-3 py-2 text-sm text-gray-500"
                    >
                        {{ _mft('crm:tag.noTagsFound') }}
                    </li>

                    <li
                        v-if="canCreate"
                        :id="optionId(createOptionIndex)"
                        role="option"
                        :aria-selected="false"
                        class="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 cursor-pointer"
                        :class="[
                            highlightedIndex === createOptionIndex ? 'bg-gray-100' : '',
                            filteredTags.length > 0 ? 'border-t border-gray-100' : '',
                            creating ? 'opacity-50 pointer-events-none' : '',
                        ]"
                        @mousedown.prevent
                        @mouseenter="highlightedIndex = createOptionIndex"
                        @click="createTag"
                    >
                        <i class="w-4 text-center fa-regular fa-plus shrink-0"></i>
                        <span class="truncate">
                            {{ _mft('crm:tag.createNew') }}: <span class="font-medium text-gray-900">{{ search.trim() }}</span>
                        </span>
                    </li>
                </template>
            </ul>

            <div
                v-if="selectMultiple && ! loading"
                class="flex items-center justify-between gap-2 px-3 py-2 border-t border-gray-100 bg-gray-50"
            >
                <button
                    type="button"
                    class="text-xs text-gray-600 mf-link"
                    :class="selectedTags.length === 0 ? 'invisible' : ''"
                    @mousedown.prevent
                    @click="clearSelection"
                >
                    {{ _mft('shared:action.clear') }}
                </button>
                <button-base
                    size="sm"
                    style-type="primary"
                    @mousedown.prevent
                    @click="closeDropdown"
                >
                    {{ _mft('shared:done') }}
                </button-base>
            </div>
        </div>
    </div>
</template>

<style>

</style>
